import React, { useState, useMemo } from 'react';
import {
  FileDown,
  FileCode,
  X,
  Layers,
  Palette,
  Loader2,
} from 'lucide-react';
import { Button } from '../../shared/components/Button';
import { DocumentModel, PaperSize, PageOrientation } from '../../core/ast/document.types';
import { DocumentRenderer } from '../../core/renderer/DocumentRenderer';

export interface ExportSettings {
  paperSize: PaperSize;
  orientation: PageOrientation;
  marginPreset: 'normal' | 'narrow' | 'wide';
  colorMode: 'color' | 'grayscale';
  targetFormat: 'pdf' | 'docx';
}

interface ExportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentModel;
  defaultFormat?: 'pdf' | 'docx';
  onExportPdf: (doc: DocumentModel, settings: ExportSettings) => Promise<void>;
  onExportDocx: (doc: DocumentModel, settings: ExportSettings) => Promise<void>;
}

export const ExportPreviewModal: React.FC<ExportPreviewModalProps> = ({
  isOpen,
  onClose,
  document: doc,
  defaultFormat = 'pdf',
  onExportPdf,
  onExportDocx,
}) => {
  const [targetFormat, setTargetFormat] = useState<'pdf' | 'docx'>(defaultFormat);
  const [exportFilename, setExportFilename] = useState<string>(() => doc.metadata.title || 'Document');
  const [paperSize, setPaperSize] = useState<PaperSize>(doc.pageSettings?.paperSize || 'A4');
  const [orientation, setOrientation] = useState<PageOrientation>(doc.pageSettings?.orientation || 'portrait');
  const [marginPreset, setMarginPreset] = useState<'normal' | 'narrow' | 'wide'>('normal');
  const [colorMode, setColorMode] = useState<'color' | 'grayscale'>('color');
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // Compute margins in mm based on preset
  const computedMargins = useMemo(() => {
    switch (marginPreset) {
      case 'narrow':
        return { topMm: 12.7, bottomMm: 12.7, leftMm: 12.7, rightMm: 12.7 };
      case 'wide':
        return { topMm: 31.8, bottomMm: 31.8, leftMm: 31.8, rightMm: 31.8 };
      case 'normal':
      default:
        return { topMm: 25.4, bottomMm: 25.4, leftMm: 25.4, rightMm: 25.4 };
    }
  }, [marginPreset]);

  // Construct updated preview document based on settings chosen in modal
  const previewDoc = useMemo<DocumentModel>(() => {
    return {
      ...doc,
      metadata: {
        ...doc.metadata,
        title: exportFilename || doc.metadata.title || 'Document',
      },
      pageSettings: {
        ...doc.pageSettings,
        paperSize,
        orientation,
        margins: computedMargins,
      },
    };
  }, [doc, exportFilename, paperSize, orientation, computedMargins]);

  // Count active pages from enabled sections
  const estimatedPageCount = useMemo(() => {
    const enabled = doc.sections.filter((s) => s.enabled);
    let pages = 0;
    let inPage = false;
    enabled.forEach((sec, idx) => {
      if (idx === 0 || sec.type === 'cover' || sec.settings?.pageBreakBefore || !inPage) {
        pages++;
        inPage = true;
      }
      if (sec.blocks.some((b) => b.type === 'page_break')) {
        pages++;
      }
    });
    return Math.max(1, pages);
  }, [doc.sections]);

  if (!isOpen) return null;

  const handleExecuteExport = async () => {
    setIsExporting(true);
    const settings: ExportSettings = {
      paperSize,
      orientation,
      marginPreset,
      colorMode,
      targetFormat,
    };

    try {
      if (targetFormat === 'pdf') {
        await onExportPdf(previewDoc, settings);
      } else {
        await onExportDocx(previewDoc, settings);
      }
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
      }}
    >
      <div
        className="card animate-fade-in"
        style={{
          width: '900px',
          maxWidth: '96vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: 'var(--space-4) var(--space-5)',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'var(--bg-surface)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--primary-100)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--primary-700)',
              }}
            >
              {targetFormat === 'pdf' ? <FileDown size={18} /> : <FileCode size={18} />}
            </div>
            <div>
              <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                Export Preview & Setup
              </h3>
              <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: 0 }}>
                Verify the exact live preview document before generating your file
              </p>
            </div>
          </div>

          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            disabled={isExporting}
            style={{ width: 30, height: 30 }}
            title="Cancel and close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Modal Body: Two Column (Left Settings, Right Scaled Live Preview) */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: '440px' }}>
          {/* Left Settings Panel */}
          <div
            style={{
              width: '320px',
              borderRight: '1px solid var(--border-subtle)',
              padding: 'var(--space-4)',
              backgroundColor: '#fafbfc',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              overflowY: 'auto',
            }}
          >
            {/* Target Format Selector */}
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '12px', fontWeight: 600 }}>
                Export Format
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                <button
                  type="button"
                  onClick={() => setTargetFormat('pdf')}
                  className={`btn ${targetFormat === 'pdf' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '12px', padding: '6px 10px', justifyContent: 'center' }}
                >
                  <FileDown size={14} /> PDF Document
                </button>
                <button
                  type="button"
                  onClick={() => setTargetFormat('docx')}
                  className={`btn ${targetFormat === 'docx' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '12px', padding: '6px 10px', justifyContent: 'center' }}
                >
                  <FileCode size={14} /> Word (.docx)
                </button>
              </div>
            </div>

            {/* Paper Size */}
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '12px', fontWeight: 600 }}>
                Paper Size
              </label>
              <select
                className="form-input"
                value={paperSize}
                onChange={(e) => setPaperSize(e.target.value as PaperSize)}
                style={{ fontSize: '12px', height: '32px' }}
              >
                <option value="A4">A4 (210 × 297 mm) — Standard Academic</option>
                <option value="Letter">Letter (8.5 × 11 in)</option>
                <option value="Legal">Legal (8.5 × 14 in)</option>
              </select>
            </div>

            {/* Orientation */}
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '12px', fontWeight: 600 }}>
                Orientation
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                <button
                  type="button"
                  onClick={() => setOrientation('portrait')}
                  className={`btn ${orientation === 'portrait' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '11px', padding: '4px 8px', justifyContent: 'center' }}
                >
                  Portrait
                </button>
                <button
                  type="button"
                  onClick={() => setOrientation('landscape')}
                  className={`btn ${orientation === 'landscape' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '11px', padding: '4px 8px', justifyContent: 'center' }}
                >
                  Landscape
                </button>
              </div>
            </div>

            {/* Margins */}
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '12px', fontWeight: 600 }}>
                Margins
              </label>
              <select
                className="form-input"
                value={marginPreset}
                onChange={(e) => setMarginPreset(e.target.value as any)}
                style={{ fontSize: '12px', height: '32px' }}
              >
                <option value="normal">Normal (25.4 mm / 1 inch)</option>
                <option value="narrow">Narrow (12.7 mm / 0.5 inch)</option>
                <option value="wide">Wide (31.8 mm / 1.25 inch)</option>
              </select>
            </div>

            {/* Color Mode */}
            <div className="form-group">
              <label className="form-label" style={{ fontSize: '12px', fontWeight: 600 }}>
                Color Output
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                <button
                  type="button"
                  onClick={() => setColorMode('color')}
                  className={`btn ${colorMode === 'color' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '11px', padding: '4px 8px', justifyContent: 'center' }}
                >
                  <Palette size={13} /> Full Color
                </button>
                <button
                  type="button"
                  onClick={() => setColorMode('grayscale')}
                  className={`btn ${colorMode === 'grayscale' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ fontSize: '11px', padding: '4px 8px', justifyContent: 'center' }}
                >
                  Grayscale
                </button>
              </div>
            </div>

            {/* Page Count & Document Info Pill */}
            <div
              style={{
                marginTop: 'auto',
                padding: 'var(--space-2) var(--space-3)',
                backgroundColor: 'var(--primary-50)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--primary-200)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '11px', color: 'var(--primary-800)', fontWeight: 600 }}>
                <Layers size={13} /> Total Pages: {estimatedPageCount}
              </div>
              <span className="badge badge-primary" style={{ fontSize: '10px' }}>
                {paperSize} • {orientation}
              </span>
            </div>
          </div>

          {/* Right Live Preview Column */}
          <div
            style={{
              flex: 1,
              backgroundColor: '#e5e7eb',
              padding: 'var(--space-4)',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              filter: colorMode === 'grayscale' ? 'grayscale(100%)' : 'none',
            }}
          >
            <div
              style={{
                transform: 'scale(0.68)',
                transformOrigin: 'top center',
                pointerEvents: 'none',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <DocumentRenderer document={previewDoc} />
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: 'var(--space-3) var(--space-5)',
            borderTop: '1px solid var(--border-subtle)',
            backgroundColor: 'var(--bg-surface)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1, marginRight: '16px' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
              File name:
            </label>
            <div style={{ display: 'flex', alignItems: 'center', maxWidth: '320px', width: '100%' }}>
              <input
                type="text"
                className="form-input"
                value={exportFilename}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/[\\/:*?"<>|]/g, '');
                  setExportFilename(sanitized);
                }}
                placeholder="Enter filename"
                style={{
                  fontSize: '12px',
                  height: '32px',
                  padding: '4px 8px',
                  fontWeight: 600,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  borderRight: 'none',
                }}
                title="Customize export file name"
              />
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0 8px',
                  height: '32px',
                  backgroundColor: 'var(--bg-subtle)',
                  border: '1px solid var(--border-default)',
                  borderLeft: 'none',
                  borderTopRightRadius: 'var(--radius-sm)',
                  borderBottomRightRadius: 'var(--radius-sm)',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                }}
              >
                .{targetFormat}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Button variant="ghost" size="sm" onClick={onClose} disabled={isExporting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={isExporting ? <Loader2 size={14} className="animate-spin" /> : targetFormat === 'pdf' ? <FileDown size={14} /> : <FileCode size={14} />}
              onClick={handleExecuteExport}
              loading={isExporting}
            >
              {isExporting ? 'Generating...' : targetFormat === 'pdf' ? 'Export PDF' : 'Export DOCX'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
